import type { MascotEmotion } from "@/lib/mascot/emotions";

/**
 * Tekst dymka i emocja maskotki Sianko (warstwa dziecka) na hero ekranów
 * ChildDashboard i ChildWallet.
 *
 * Styl Sianka:
 * - Lekki, ciepły, bez presji.
 * - Krótkie zdania (≤ ~40 znaków, mieści się w bubble 150px).
 * - Emoji na końcu, nie spam.
 * - Bez „musisz", bez „dobre dziecko" — naturalne wsparcie i celebracja.
 *
 * Wybór tekstu zależy od `moment` (kontekstu). Każdy moment ma:
 * - pulę kilku wariantów rotowanych deterministycznie per dzień
 *   (ten sam dzień = ten sam tekst, kolejny dzień = nowy wariant),
 * - sugerowaną emocję (mapowaną dalej na pozę przez `poseFor`).
 *
 * Jeden source of truth — tu dodajemy nowe momenty i warianty.
 */

export type ChildMascotMoment =
  // ── ChildDashboard ──
  | "dashboard-empty" // 0 zadań na dziś
  | "dashboard-doing-start" // ma zadania, jeszcze nic nie zrobione
  | "dashboard-doing-mid" // w trakcie (część zrobiona, część zostaje)
  | "dashboard-waiting" // wszystkie zgłoszone, czeka na zatwierdzenie
  | "dashboard-doing-end" // wszystkie zadania dnia zaliczone
  | "dashboard-goal-start" // ma cel, balance < 25% celu
  | "dashboard-goal-mid" // ma cel, balance 25-75% celu
  | "dashboard-goal-almost" // ma cel, balance ≥ 75% celu (ale < 100)
  | "dashboard-goal-reached" // ma cel, balance ≥ 100% celu
  // ── ChildWallet ──
  | "wallet-empty" // saldo = 0
  | "wallet-saving" // saldo > 0
  | "wallet-goal-reached"; // saldo ≥ cel

type Args = {
  /** Imię dziecka (bez nazwiska). Pusty string = bezimiennie. */
  name?: string;
  /** Forma żeńska czasowników (Iga: true, Leo: false). */
  female?: boolean;
  /** Saldo dziecka w groszach. */
  balanceMinor?: number;
  /** Tytuł aktywnego celu (do podstawienia w {goal}). */
  goalTitle?: string;
  /** Emoji aktywnego celu. */
  goalEmoji?: string;
  /** Brakująca kwota do celu w PLN (zaokrąglona w górę). */
  remainingZl?: number;
  /** Liczba zadań zrobionych dziś. */
  done?: number;
  /** Łączna liczba zadań na dziś. */
  total?: number;
};

export type ChildSpeech = {
  text: string;
  emotion: MascotEmotion;
};

const dayOfYear = (d: Date): number => {
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = +d - +start;
  return Math.floor(diff / 86400000);
};

const pick = <T>(arr: readonly T[], seed: number): T =>
  arr[Math.abs(seed) % arr.length];

/**
 * Seed zmieniający się 4× dziennie (co 6h: 00-06, 06-12, 12-18, 18-24)
 * + reagujący na stan (done/total). Dzięki temu maskotka mówi inny
 * tekst po każdej porze dnia ORAZ po każdej zmianie postępu zadań,
 * nie tylko raz dziennie.
 */
const TIME_SLOTS_PER_DAY = 4;
const timeSlot = (d: Date): number => Math.floor(d.getHours() / 6);

const computeSeed = (a: Args): number => {
  const now = new Date();
  const base = dayOfYear(now) * TIME_SLOTS_PER_DAY + timeSlot(now);
  const stateMod = (a.done ?? 0) + (a.total ?? 0);
  return base + stateMod;
};

// ─── Pule tekstów ────────────────────────────────────────────────
// Każdy entry to funkcja args → string, dzięki czemu może wstawić imię,
// kwotę, tytuł celu itd. Brak gendered czasowników w większości — Sianko
// używa neutralnych form ("Lecimy!", "Działamy!"). Gdzie czasownik
// musi być w 1. lub 2. os., używamy `female` z Args.

type Speech = (a: Args) => string;

const greeting = (a: Args): string =>
  a.name ? `Cześć ${a.name}!` : "Cześć!";

// 0 zadań na dziś
const DASHBOARD_EMPTY: readonly Speech[] = [
  () => "Wolny dzień ☀️",
  () => "Może zaproponuj zadanie?",
  (a) => `${greeting(a)} Dziś luz 🌳`,
  () => "Czas na świeże powietrze 🌳",
  () => "Ale cisza dziś 🌳",
  () => "Dziś bardziej życie niż ekran ✨",
  () => "Brak planu też bywa planem 😄",
  () => "Może czas na rower albo spacer?",
  () => "Sianko dziś odpoczywa ☀️",
  () => "Luzik. Nabieramy mocy 🌱",
  (a) => (a.name ? `Hej ${a.name}, spokojny dzień?` : "Hej, spokojny dzień?"),
  () => "Można dziś trochę poleniuchować 😴",
  () => "Dziś bez odhaczania 😄",
  () => "Czasem nic też jest okej 💛",
];

// Ma zadania, jeszcze nic nie zrobione
const DASHBOARD_DOING_START: readonly Speech[] = [
  (a) => `${greeting(a)} Lecimy! 💪`,
  (a) => `${greeting(a)} Zaczynamy ✨`,
  (a) => `${greeting(a)} Mała rozgrzewka?`,
  () => "Działamy małymi krokami 🌱",
  () => "Startujemy powoli 🌱",
  () => "Jedno zadanie na początek?",
  () => "Nie ścigamy się 💛",
  () => "Mały krok i lecimy ✨",
  () => "Dobra, zobaczmy co dziś mamy 😄",
  () => "Dasz radę po swojemu 💪",
  () => "Najpierw jedno, potem reszta 🌾",
  (a) => (a.name ? `Cześć ${a.name}, działamy?` : "Cześć, działamy?"),
  () => "Spokojnie, bez pośpiechu ✨",
  () => "Każdy kiedyś zaczynał 🌱",
];

// Wszystko zgłoszone, ale jeszcze nic nie zatwierdzone — Sianko czeka
// cierpliwie razem z dzieckiem.
const DASHBOARD_WAITING: readonly Speech[] = [
  () => "Czeka 🕒",
  () => "Sianko czeka cierpliwie ✨",
  () => "Zaraz rodzic to klepnie 💪",
  (a) =>
    a.done && a.total
      ? `${a.done}/${a.total} czeka na zatw.`
      : "Czeka na zatwierdzenie",
  () => "Wszystko zgłoszone 🌾",
  () => "Już prawie ✨",
  () => "Spokojnie, czekamy razem 💛",
  () => "Cisza przed klepnięciem 😄",
  () => "Sianko trzyma kciuki 🤞",
  () => "Krótka chwilka i gotowe 🌱",
  () => "Złóż ręce, idzie potwierdzenie 😎",
  () => "Czeka na rodzica 🕒",
];

// W trakcie (część zrobiona, część zostaje)
const DASHBOARD_DOING_MID: readonly Speech[] = [
  (a) =>
    a.done && a.total
      ? `${a.female ? "Zrobiłaś" : "Zrobiłeś"} już ${a.done} z ${a.total}! ${
          a.done >= Math.ceil(a.total / 2) ? "🔥" : "✨"
        }`
      : "Idzie nam świetnie ✨",
  (a) =>
    a.done && a.total
      ? `Połowa drogi za nami! ${a.done}/${a.total}`
      : "Połowa drogi za nami!",
  () => "Tak trzymaj 💪",
  () => "Jeden po drugim, jeden po drugim ✨",
  () => "Powoli do przodu 💪",
  () => "Już coś za Tobą ✨",
  () => "Nieźle Ci idzie 🌱",
  () => "Krok po kroku 😄",
  () => "Coraz mniej zostało 💛",
  () => "Dobra robota, serio 🌟",
  () => "Robi się konkretnie 🔥",
  () => "Systematyczność wygrywa 🌾",
  () => "Już widać progres ✨",
  () => "Nie idealnie. Wystarczająco 💛",
];

// Wszystkie zadania dnia zaliczone
const DASHBOARD_DOING_END: readonly Speech[] = [
  () => "Komplet dnia! 🎉",
  () => "Brawo, koniec na dziś! ✨",
  (a) => `${a.female ? "Dałaś" : "Dałeś"} radę! 🌟`,
  () => "Wyjdź na dwór 🌳",
  () => "Gotowe. I tyle wystarczy 🌟",
  () => "Dziś robota zrobiona 💪",
  () => "Piękny komplet 😄",
  () => "Teraz odpoczynek 🌳",
  () => "Można zamykać dzień ✨",
  () => "Brawo za konsekwencję 🌾",
  (a) => (a.name ? `Dobra robota, ${a.name}! 💛` : "Dobra robota! 💛"),
  () => "Wszystko odhaczone 🎉",
  () => "Pełen wynik dziś 😎",
  () => "Czas na coś fajnego 🌳",
];

// Goal: < 25% celu (start)
const DASHBOARD_GOAL_START: readonly Speech[] = [
  (a) => `${greeting(a)} Świetny start! 🌱`,
  (a) =>
    a.goalTitle
      ? `Zbieramy na ${a.goalTitle} ${a.goalEmoji ?? "🎯"}`
      : "Zbieramy na cel ✨",
  () => "Mała kropla, wielka rzeka 🌱",
  () => "Każdy cel zaczyna się mało 🌱",
  () => "Powoli rośnie 💛",
  () => "Pierwsze kroki są ważne ✨",
  (a) =>
    a.goalTitle
      ? `${a.goalTitle} coraz bliżej 😄`
      : "Cel coraz bliżej 😄",
  () => "Trochę dziś, trochę jutro 🌾",
  (a) => (a.name ? `Dobra droga, ${a.name} 💪` : "Dobra droga 💪"),
  () => "To się zbiera szybciej niż myślisz ✨",
  () => "Już coś jest odłożone 💰",
  () => "Mały start też się liczy 🌱",
  () => "Każda złotówka robi robotę 💛",
];

// Goal: 25-75% celu (mid)
const DASHBOARD_GOAL_MID: readonly Speech[] = [
  (a) =>
    a.remainingZl != null && a.goalTitle
      ? `${a.remainingZl} zł do ${a.goalTitle}! ${a.goalEmoji ?? "🎯"}`
      : "Idzie nam świetnie ✨",
  () => "Połowa drogi za nami! 💪",
  (a) =>
    a.remainingZl != null
      ? `Jeszcze ${a.remainingZl} zł — dasz radę!`
      : "Dasz radę!",
  () => "Robi się poważnie 💪",
  (a) =>
    a.goalTitle
      ? `${a.goalTitle} już prawie Cię widzi 😄`
      : "Cel już prawie Cię widzi 😄",
  () => "Coraz bliżej celu ✨",
  () => "Świetne tempo 🌾",
  () => "Nie odpuszczasz, co? 🔥",
  () => "Już kawał drogi za Tobą 💛",
  () => "To naprawdę rośnie 💰",
  () => "Regularność działa ✨",
  () => "Jeszcze trochę i będzie wow 😄",
  () => "Dobrze to wygląda 🌱",
];

// Goal: ≥ 75% celu (almost)
const DASHBOARD_GOAL_ALMOST: readonly Speech[] = [
  (a) =>
    a.goalTitle
      ? `Już prawie! ${a.goalTitle} czeka ${a.goalEmoji ?? "🎯"}`
      : "Już prawie! ✨",
  () => "Sprint do celu! 🏁",
  (a) =>
    a.remainingZl != null
      ? `Tylko ${a.remainingZl} zł i jest!`
      : "Jeszcze chwila!",
  () => "Końcówka zawsze smakuje najlepiej 🏁",
  () => "To już prawie meta 💪",
  () => "Za chwilę będzie świętowanie 🎉",
  (a) =>
    a.goalTitle ? `${a.goalTitle} już czeka ✨` : "Cel już czeka ✨",
  () => "Ostatnia prosta 🌾",
  () => "Naprawdę blisko 😄",
  () => "Już prawie można odbierać 💛",
  () => "Jeszcze chwila i gotowe 🌟",
  () => "To się zaraz wydarzy 🔥",
  () => "No dobra… imponujące 😎",
];

// Goal: ≥ 100% celu (reached)
const DASHBOARD_GOAL_REACHED: readonly Speech[] = [
  (a) => (a.goalTitle ? `${a.goalTitle} zdobyte! 🎉` : "Cel zdobyty! 🎉"),
  (a) =>
    a.female
      ? "Uzbierałaś całą kwotę! 💛"
      : "Uzbierałeś całą kwotę! 💛",
  () => "Możesz odebrać u rodzica ✨",
];

// Wallet: saldo 0
const WALLET_EMPTY: readonly Speech[] = [
  () => "Zacznij zbierać! ✨",
  () => "Pierwsze zadanie czeka 🌱",
  (a) => `${greeting(a)} Zaczynamy?`,
  () => "Portfel jeszcze śpi 😴",
  () => "Każdy kiedyś miał zero 🌱",
  () => "Od czegoś trzeba zacząć ✨",
  () => "Pierwsze złotówki są najlepsze 💛",
  (a) => (a.name ? `Hej ${a.name}, zbieramy?` : "Hej, zbieramy?"),
  () => "Dziś zero, jutro kto wie 😄",
  () => "Pierwszy krok robi różnicę 🌾",
  () => "Może jedno małe zadanie?",
  () => "Spokojnie, wszystko przed Tobą 💪",
  () => "Tu się dopiero rozkręcamy ✨",
];

// Wallet: saldo > 0
const WALLET_SAVING: readonly Speech[] = [
  (a) =>
    a.balanceMinor != null
      ? `${a.female ? "Uzbierałaś" : "Uzbierałeś"} już ${
          (a.balanceMinor / 100).toFixed(2).replace(".", ",")
        } zł! 💰`
      : "Skarbonka rośnie! 💰",
  () => "Każdy grosz się liczy ✨",
  (a) => `Tak trzymaj${a.name ? `, ${a.name}` : ""}! 💪`,
  () => "Ładnie rośnie 💰",
  () => "To już coś konkretnego ✨",
  () => "Coraz więcej w skarbonce 🌾",
  () => "System działa 😄",
  (a) =>
    a.name ? `Dobrze to wygląda, ${a.name} 💪` : "Dobrze to wygląda 💪",
  () => "Powoli robi się suma 💛",
  () => "Regularność robi magię 🌱",
  () => "Zbieranie ma sens ✨",
  () => "Każda złotówka ma znaczenie 💰",
  () => "Sianko jest dumne 🌟",
];

// Wallet: cel osiągnięty (saldo ≥ goal)
const WALLET_GOAL_REACHED: readonly Speech[] = [
  () => "Cel osiągnięty! 🎉",
  () => "Możesz odebrać wypłatę 💛",
  (a) =>
    a.female ? "Uzbierałaś całą kwotę! 🎉" : "Uzbierałeś całą kwotę! 🎉",
];

// ─── Mapowanie moment → pula tekstów + emocja ────────────────────

const MOMENT_POOLS: Record<ChildMascotMoment, readonly Speech[]> = {
  "dashboard-empty": DASHBOARD_EMPTY,
  "dashboard-doing-start": DASHBOARD_DOING_START,
  "dashboard-doing-mid": DASHBOARD_DOING_MID,
  "dashboard-waiting": DASHBOARD_WAITING,
  "dashboard-doing-end": DASHBOARD_DOING_END,
  "dashboard-goal-start": DASHBOARD_GOAL_START,
  "dashboard-goal-mid": DASHBOARD_GOAL_MID,
  "dashboard-goal-almost": DASHBOARD_GOAL_ALMOST,
  "dashboard-goal-reached": DASHBOARD_GOAL_REACHED,
  "wallet-empty": WALLET_EMPTY,
  "wallet-saving": WALLET_SAVING,
  "wallet-goal-reached": WALLET_GOAL_REACHED,
};

const MOMENT_EMOTIONS: Record<ChildMascotMoment, MascotEmotion> = {
  "dashboard-empty": "outdoor",
  "dashboard-doing-start": "focused",
  "dashboard-doing-mid": "focused",
  "dashboard-waiting": "waiting",
  "dashboard-doing-end": "proud",
  "dashboard-goal-start": "happy",
  "dashboard-goal-mid": "saving",
  "dashboard-goal-almost": "celebrate",
  "dashboard-goal-reached": "celebrate",
  "wallet-empty": "thinking",
  "wallet-saving": "saving",
  "wallet-goal-reached": "celebrate",
};

// ─── Główna funkcja ──────────────────────────────────────────────

/**
 * Zwraca tekst dymka i sugerowaną emocję dla maskotki w danym kontekście.
 * Tekst rotuje się deterministycznie per dzień (seed = day of year).
 */
export function childMascotSpeech(
  moment: ChildMascotMoment,
  args: Args = {},
): ChildSpeech {
  const seed = computeSeed(args);
  const speech = pick(MOMENT_POOLS[moment], seed);
  return {
    text: speech(args),
    emotion: MOMENT_EMOTIONS[moment],
  };
}
